import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { getAgencyOrders } from "../../../api/agency-orders/AgencyOrdersService";
import { formatOrderDate } from "../../../utils/dateOrders.utils";
import styles from "./index.module.css";
import HandleRequests from "../../Shared/handleRequests/HandleRequests";
import { updateLocation } from "../../../store/slices/localSlice";
import { useDispatch } from "react-redux";

const statusColors = {
  Pendiente: "warning",
  Aprobado: "info",
  Completado: "success",
  Rechazado: "error",
  Cancelado: "default",
};

const statusLabels = {
  Pendiente: "Pendiente",
  Aprobado: "Aprobado",
  Completado: "Completado",
  Rechazado: "Rechazado",
  Cancelado: "Cancelado",
};

export const Pedidos = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userAgencies = useSelector(
    (state) => state.myAgencies?.user_agencies || [],
  );
  const { textColor } = useSelector((state) => state.local);
  const colors = useSelector((state) => state.local.uiConfig);

  const [allOrders, setAllOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(updateLocation({ pedidos: true }));
  }, [dispatch]);

  useEffect(() => {
    if (userAgencies.length > 0) {
      loadAllOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAgencies]);

  useEffect(() => {
    // Aplicar paginación del lado del cliente
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const filteredOrders = statusFilter
      ? allOrders.filter((order) => order.status === statusFilter)
      : allOrders;

    // Ordenar por fecha de solicitud (más recientes primero)
    const sortedOrders = [...filteredOrders].sort((a, b) => {
      const dateA = new Date(a.requestDate || 0);
      const dateB = new Date(b.requestDate || 0);
      return dateB - dateA;
    });

    setTotalElements(sortedOrders.length);
    setTotalPages(Math.ceil(sortedOrders.length / size));
    setOrders(sortedOrders.slice(startIndex, endIndex));
  }, [allOrders, page, size, statusFilter]);

  const loadAllOrders = async () => {
    if (userAgencies.length === 0) return;

    setLoading(true);
    setError(false);
    try {
      // Obtener todas las agencias (incluyendo subagencias)
      const allAgencyIds = [];
      userAgencies.forEach((agency) => {
        if (agency.id) {
          allAgencyIds.push(agency.id);
        }
        // Incluir subagencias si existen
        if (agency.childrenAgencies && Array.isArray(agency.childrenAgencies)) {
          agency.childrenAgencies.forEach((subAgency) => {
            if (subAgency.id) {
              allAgencyIds.push(subAgency.id);
            }
          });
        }
      });

      // Hacer llamadas paralelas para todas las agencias
      const orderPromises = allAgencyIds.map((agencyId) =>
        getAgencyOrders(agencyId, { page: 0, size: 1000 }).catch((err) => {
          console.error(`Error loading orders for agency ${agencyId}:`, err);
          return { content: [] }; // Retornar array vacío en caso de error
        }),
      );

      const responses = await Promise.all(orderPromises);

      // Combinar todos los pedidos
      const combinedOrders = responses.flatMap((response) => response.content || []);

      // Eliminar duplicados por ID (por si acaso)
      const uniqueOrders = combinedOrders.filter(
        (order, index, self) => index === self.findIndex((o) => o.id === order.id),
      );

      setAllOrders(uniqueOrders);
    } catch (err) {
      console.error("Error loading orders:", err);
      setError(true);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePagination = (event, value) => {
    setPage(value - 1); // Material UI pagination is 1-based, API is 0-based
  };

  const handleChangeSize = (event) => {
    setSize(event.target.value);
    setPage(0);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const getTotalRequested = (details) => {
    return details?.reduce((sum, detail) => sum + (detail.quantityRequested || 0), 0) || 0;
  };

  const getTotalApproved = (details) => {
    return details?.reduce((sum, detail) => sum + (detail.quantityApproved || 0), 0) || 0;
  };

  if (userAgencies.length === 0) {
    return (
      <Grid container sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No hay agencias disponibles para mostrar los pedidos.
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ p: 3 }}>
      <HandleRequests loading={loading && !orders.length} error={error}>
        <Grid container spacing={2}>
          {/* Header */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ShoppingCartIcon
                  sx={{
                    fontSize: "2rem",
                    color: colors?.primary?.main || "primary.main",
                  }}
                />
                <Typography variant="h4" component="h1">
                  Pedidos
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/main/pedidos/nuevo")}
                sx={{ backgroundColor: colors?.primary?.main }}
              >
                Crear Nuevo Pedido
              </Button>
            </Box>
          </Grid>

          {/* Filters */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  label="Estado"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Pendiente">Pendiente</MenuItem>
                  <MenuItem value="Aprobado">Aprobado</MenuItem>
                  <MenuItem value="Completado">Completado</MenuItem>
                  <MenuItem value="Rechazado">Rechazado</MenuItem>
                  <MenuItem value="Cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Número de Pedido</TableCell>
                    <TableCell>Agencia</TableCell>
                    <TableCell>Fecha de Solicitud</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Cant. Solicitada</TableCell>
                    <TableCell align="right">Cant. Aprobada</TableCell>
                    <TableCell>Fecha de Aprobación</TableCell>
                    <TableCell>Fecha de Entrega</TableCell>
                    <TableCell>Número de Remito</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No hay pedidos para mostrar
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            #{order.nroOrder}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {order.agency
                            ? `${order.agency.number || ""}${order.agency.subNumber != null && order.agency.subNumber !== 0 ? ` - ${order.agency.subNumber}` : ""}${order.agency.name ? ` - ${order.agency.name}` : ""}`
                            : order.agencyName || order.agencyNumber
                            ? `${order.agencyNumber || ""}${order.agencyNumber && order.agencyName ? " - " : ""}${order.agencyName || ""}`
                            : "-"}
                        </TableCell>
                        <TableCell>{formatOrderDate(order.requestDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabels[order.status] || order.status}
                            color={statusColors[order.status] || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {getTotalRequested(order.details).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {getTotalApproved(order.details) > 0
                            ? getTotalApproved(order.details).toLocaleString()
                            : "-"}
                        </TableCell>
                        <TableCell>{formatOrderDate(order.approvalDate)}</TableCell>
                        <TableCell>{formatOrderDate(order.deliveryDate)}</TableCell>
                        <TableCell>
                          {order.deliveryNoteId ? `#${order.deliveryNoteId}` : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => navigate(`/main/pedidos/${order.id}`)}
                          >
                            Ver Detalle
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Pagination */}
          {totalPages > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <FormControl sx={{ minWidth: 120 }}>
                  <Select value={size} onChange={handleChangeSize}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={handlePagination}
                    color="secondary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Total: {totalElements} pedidos
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </HandleRequests>
    </Grid>
  );
};



